import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Error, Loader, LoaderView } from '@utilities';
import { readInstantSurvey, readSurveyResponsesSummaryGPT } from '@api/instant_survey';
import { setInstantSurveyAuthToken } from '@api/instant_survey/api';
import { useSearchParams } from 'react-router-dom';
import { readSurvey } from '@api/surveys';
import { readGlobalFilters, readSurveyDashboardRequests } from '@api/survey_dashboard';

import Responses from '../../../../../Survey/components/Dashboard/components/Responses';
import { DISPLAY_AS } from '../../../../../Survey/components/Dashboard/components/Responses/utilities/helpers';
import { SEARCH_PARAM_KEYS } from '../../../../../Survey/utilities/constants';

const AskWhyResponses = ({ publicToken, surveyId }) => {
  const [displayAs, setDisplayAs] = useState(DISPLAY_AS.PERCENT);
  const [globalFilters, setGlobalFilters] = useState();
  const [globalFiltersSurveyId, setGlobalFiltersSurveyId] = useState();
  const [globalFiltersError, setGlobalFiltersError] = useState();
  const [isFiltersLoading, setIsFiltersLoading] = useState(true);
  const [isResponseDataLoading, setIsResponseDataLoading] = useState(true);
  const [responseData, setResponseData] = useState();
  const [responseError, setResponseError] = useState();
  const [survey, setSurvey] = useState();
  const [surveyError, setSurveyError] = useState();
  const [isSurveyLoading, setIsSurveyLoading] = useState(true);
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [surveyResponseSummariesMap, setSurveyResponseSummariesMap] = useState({});
  const [instantSurveyStatus, setInstantSurveyStatus] = useState(null);

  const responsesRequestController = useRef();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!surveyId) return;

    let isMounted = true;
    const controller = new AbortController();
    setIsSurveyLoading(true);
    setSurveyError(null);

    const loadSurveyDetails = async () => {
      try {
        const surveyDetails = await readSurvey({
          id: surveyId,
          publicToken,
          signal: controller.signal,
        });
        if (!isMounted) return;
        if (publicToken && surveyDetails?.serviceToken) {
          setInstantSurveyAuthToken(surveyDetails.serviceToken);
        }
        setSurvey(surveyDetails);

        if (!surveyDetails?.atlasInstantSurveyRef) {
          setInstantSurveyStatus('PROCESSING');
          setSurveyQuestions([]);
          return;
        }

        try {
          const instantSurvey = await readInstantSurvey({
            id: surveyDetails.atlasInstantSurveyRef,
            signal: controller.signal,
          });
          if (!isMounted) return;
          setInstantSurveyStatus(instantSurvey?.status || null);
          const normalizedQuestions =
            instantSurvey?.questions?.map((question) => {
              return {
                ...question,
                options: question.answers || [],
                title: question.text,
              };
            }) || [];

          setSurveyQuestions(normalizedQuestions);
        } catch (error) {
          if (!controller.signal.aborted && isMounted) {
            setSurveyError(error);
          }
        }
      } catch (error) {
        if (!controller.signal.aborted && isMounted) {
          setSurveyError(error);
          setInstantSurveyStatus(null);
        }
      } finally {
        if (!controller.signal.aborted && isMounted) {
          setIsSurveyLoading(false);
        }
      }
    };

    loadSurveyDetails();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [publicToken, surveyId]);

  useEffect(() => {
    if (!surveyId) return;

    const controller = new AbortController();
    setIsFiltersLoading(true);
    setGlobalFiltersError(null);
    setGlobalFilters(undefined);
    setGlobalFiltersSurveyId(undefined);

    readGlobalFilters({ publicToken, signal: controller.signal, surveyId })
      .then((filters) => {
        setGlobalFilters(filters);
        setGlobalFiltersSurveyId(surveyId);
        setIsFiltersLoading(false);
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setGlobalFiltersError(error);
          setIsFiltersLoading(false);
        }
      });

    return () => controller.abort();
  }, [publicToken, surveyId]);

  const compareByOptions = useMemo(() => {
    const primaryOptions = [
      { label: 'Overall', value: 'overall' },
      ...(globalFilters?.quotaGroups?.length > 1
        ? [{ label: 'Quota Group', value: 'quota_group' }]
        : []),
    ];
    const demographicOptions =
      globalFilters?.demographicFilters
        ?.filter(({ compareBy }) => compareBy)
        .map(({ id, label }) => ({
          label,
          value: id,
        })) || [];

    return [
      {
        label: 'Primary Segments',
        options: primaryOptions,
      },
      {
        label: 'Demographic Segments',
        options: demographicOptions,
      },
    ];
  }, [globalFilters]);

  const selectedCompareBy = useMemo(() => {
    if (!globalFilters) return null;
    const primaryOptions = compareByOptions[0]?.options || [];
    const demographicOptions = compareByOptions[1]?.options || [];

    const compareByValue = searchParams.get(SEARCH_PARAM_KEYS.COMPARE_BY);
    if (!compareByValue) {
      if (globalFilters?.quotaGroups?.length > 1 && primaryOptions[1]) {
        return primaryOptions[1];
      }

      return primaryOptions[0] || null;
    }

    return [...primaryOptions, ...demographicOptions].find(({ value }) => value === compareByValue);
  }, [compareByOptions, globalFilters, searchParams]);

  const selectedCompareByValue = selectedCompareBy?.value || '';

  const selectedDemographicFilters = useMemo(() => {
    if (!globalFilters?.demographicFilters?.length) return {};

    const searchParamsObject = Object.fromEntries(searchParams.entries());
    return Object.keys(searchParamsObject)
      .filter((key) => globalFilters.demographicFilters.some(({ id }) => id === key))
      .reduce((acc, key) => {
        acc[key] = searchParamsObject[key]?.split(',');
        return acc;
      }, {});
  }, [globalFilters, searchParams]);

  const selectedOptionIds =
    searchParams.get(SEARCH_PARAM_KEYS.QUESTION_OPTION)?.split(',').filter(Boolean) || [];

  const selectedQuotaGroups = useMemo(() => {
    if (!globalFilters?.quotaGroups?.length) return [];

    const groupSearchParam = searchParams.get(SEARCH_PARAM_KEYS.QUOTA_GROUP);
    if (!groupSearchParam) return [];

    return groupSearchParam
      .split(',')
      .map((groupRef) =>
        globalFilters.quotaGroups.find(
          ({ instantSurveyGroupRef }) => instantSurveyGroupRef === groupRef
        )
      )
      .filter(Boolean);
  }, [globalFilters, searchParams]);

  const defaultCompareByValue = compareByOptions[0]?.options?.[0]?.value;

  const exportQuery = useMemo(
    () => ({
      compare_by:
        selectedCompareBy?.value && selectedCompareBy?.value !== defaultCompareByValue
          ? selectedCompareBy.value
          : '',
      option_filter: selectedOptionIds.join(';'),
      quota_group_filter: selectedQuotaGroups
        ?.flatMap(({ quotaGroupRefs }) => quotaGroupRefs)
        .join(';'),
      ...Object.fromEntries(
        Object.entries(selectedDemographicFilters).map(([key, value]) => [key, value.join(';')])
      ),
    }),
    [
      defaultCompareByValue,
      selectedCompareBy,
      selectedOptionIds,
      selectedQuotaGroups,
      selectedDemographicFilters,
    ]
  );

  const exportQueryKey = useMemo(() => JSON.stringify(exportQuery), [exportQuery]);

  useEffect(() => {
    if (!survey?.id || !selectedCompareByValue) return;

    const controller = new AbortController();

    readSurveyResponsesSummaryGPT({
      signal: controller.signal,
      survey_id: survey.id,
    })
      .then((result) => {
        setSurveyResponseSummariesMap(result);
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.warn('[AskWhyResponses] Failed to fetch summaries', error);
          setSurveyResponseSummariesMap({});
        }
      });

    return () => controller.abort();
  }, [selectedCompareByValue, survey]);

  useEffect(() => {
    if (
      !globalFilters ||
      globalFiltersSurveyId !== surveyId ||
      !surveyQuestions.length ||
      !selectedCompareByValue
    )
      return;

    responsesRequestController.current?.abort();
    const controller = new AbortController();
    responsesRequestController.current = controller;

    setIsResponseDataLoading(true);
    setResponseData(null);
    setResponseError(null);

    const params = {
      publicToken,
      query: exportQuery,
      signal: controller.signal,
      surveyId,
    };

    const fetchResponseData = async () => {
      try {
        const { anyResponseFulfilled, responseData } = await readSurveyDashboardRequests({
          params,
          surveyQuestions,
        });

        if (controller.signal.aborted) return;

        if (!anyResponseFulfilled) {
          setResponseError(new Error('Failed to fetch any response data for this survey.'));
          setIsResponseDataLoading(false);
          return;
        }

        setResponseData(
          responseData.reduce((acc, response) => {
            if (response?.id != null) acc[response.id] = response;
            return acc;
          }, {})
        );
        setIsResponseDataLoading(false);
      } catch (error) {
        if (!controller.signal.aborted) {
          setResponseError(error);
          setIsResponseDataLoading(false);
        }
      }
    };
    fetchResponseData();

    return () => controller.abort();
  }, [exportQueryKey, globalFiltersSurveyId, publicToken, surveyId, surveyQuestions.length]);
  if (surveyError) return <Error status={surveyError?.response?.status} />;
  if (isSurveyLoading) return <Loader isCentered />;
  if (
    instantSurveyStatus &&
    instantSurveyStatus !== 'COMPLETED' &&
    instantSurveyStatus !== 'MET_SAMPLE'
  ) {
    return (
      <LoaderView
        heading="Status: Processing..."
        messages={['Your report is being created and will be available soon.']}
      />
    );
  }

  return (
    <Responses
      compareByOptions={compareByOptions}
      displayAs={displayAs}
      exportQuery={exportQuery}
      globalFilters={globalFilters}
      globalFiltersError={globalFiltersError}
      hideQuotaGroupFilters
      isAskWhySurvey
      isDataLoading={isResponseDataLoading}
      isFiltersLoading={isFiltersLoading}
      onDisplayAsChange={setDisplayAs}
      responseData={responseData}
      responseError={responseError}
      selectedCompareBy={selectedCompareBy}
      survey={survey}
      surveyId={surveyId}
      surveyQuestions={surveyQuestions}
      surveyResponseSummariesMap={surveyResponseSummariesMap}
    />
  );
};

AskWhyResponses.propTypes = {
  publicToken: PropTypes.string,
  surveyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default AskWhyResponses;
